import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { formatDate } from "@/lib/format";
import { ArrowLeft, Headphones, ExternalLink } from "lucide-react";
import EventImage from "@/components/EventImage";

const LINK_TYPE_LABELS = { event: "Événement", artist: "Artiste", professional: "Artisan / Pro" };
const externalCta = (section) =>
  section === "podcast" ? { label: "Écouter", Icon: Headphones }
    : section === "media" ? { label: "Découvrir", Icon: ExternalLink }
      : { label: "Visiter le site", Icon: ExternalLink };

function renderBody(body) {
  return body.split("\n").map((line, i) => {
    if (line.startsWith("## ")) return <h2 key={i} className="font-serif text-2xl text-amber-200/90 mt-10 mb-3">{line.slice(3)}</h2>;
    if (line.trim() === "") return null;
    const parts = line.split(/(\*\*[^*]+\*\*)/g).map((p, j) => p.startsWith("**") && p.endsWith("**") ? <strong key={j} className="text-amber-100">{p.slice(2, -2)}</strong> : p);
    return <p key={i} className="text-lg text-stone-300/90 leading-relaxed mb-4">{parts}</p>;
  });
}

export default function Article() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const { data: a, isLoading } = useQuery({ queryKey: ["article", slug], queryFn: async () => (await api.get(`/articles/${slug}`)).data });

  if (isLoading) return <div className="py-24 text-center text-stone-400">{t("loading")}</div>;
  if (!a) return <div className="py-24 text-center text-stone-400">404</div>;

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Article",
    headline: a.title, description: a.excerpt, image: [a.cover],
    datePublished: a.published_at, author: { "@type": "Organization", name: a.author },
    publisher: { "@type": "Organization", name: "swiss-pagan.ch" },
  };

  return (
    <>
      <Seo title={a.title} description={a.excerpt} image={a.cover} jsonLd={jsonLd} canonicalPath={`/campfire/${slug}`} />
      <div className="relative h-[46vh] min-h-[340px] overflow-hidden">
        <EventImage src={a.cover} alt={a.title} imgClassName="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0B0C0E 5%, rgba(11,12,14,0.35) 70%)" }} />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 w-full pb-10">
            <Link to="/campfire" className="inline-flex items-center gap-1.5 text-stone-300 hover:text-amber-400 text-sm mb-4"><ArrowLeft className="w-4 h-4" /> {t("art_back")}</Link>
            <h1 className="font-serif text-3xl sm:text-5xl text-amber-100 text-glow leading-tight">{a.title}</h1>
            <div className="font-mono text-xs text-stone-400 mt-4">{a.author} · {formatDate(a.published_at, lang)} · {a.read_minutes} {t("art_read")}</div>
          </div>
        </div>
      </div>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <p className="text-xl text-stone-200 leading-relaxed mb-8 font-cormorant italic border-l-2 border-amber-700/50 pl-5">{a.excerpt}</p>
        {a.external_url && (() => { const c = externalCta(a.section); return (
          <a href={a.external_url} target="_blank" rel="noopener noreferrer" data-testid="article-external-cta"
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-5 py-2.5 rounded-md text-sm mb-8 transition-colors">
            <c.Icon className="w-4 h-4" /> {c.label} <ExternalLink className="w-3.5 h-3.5" />
          </a>
        ); })()}
        {renderBody(a.body)}
        {a.tags?.length > 0 && <div className="flex flex-wrap gap-2 mt-10">{a.tags.map((tg) => <span key={tg} className="px-3 py-1 rounded-full text-xs font-mono text-stone-400 bg-[#181A20] border border-[#272A34]">#{tg}</span>)}</div>}

        {a.linked_entities?.length > 0 && (
          <div className="mt-12 border-t border-amber-900/30 pt-8" data-testid="article-linked">
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-amber-500/80 mb-4">Fiches liées</div>
            <div className="grid sm:grid-cols-2 gap-4">
              {a.linked_entities.map((e) => (
                <Link key={`${e.type}-${e.slug}`} to={e.url} data-testid={`article-link-${e.type}-${e.slug}`} className="group flex items-center gap-4 rounded-lg border border-[#272A34] hover:border-amber-500/50 bg-[#121418] p-3 transition-all">
                  <div className="w-16 h-16 rounded-md overflow-hidden shrink-0"><EventImage src={e.image} alt={e.title} imgClassName="w-full h-full object-cover" /></div>
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-amber-500/80 mb-0.5">{LINK_TYPE_LABELS[e.type]}</div>
                    <div className="font-serif text-stone-100 truncate group-hover:text-amber-100">{e.title}</div>
                    {e.subtitle && <div className="text-xs text-stone-500 truncate">{e.subtitle}</div>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </>
  );
}
